import { type DesktopPlatform, electronPlatform, isElectronDesktop } from './electron-bridge';

/**
 * The user's operating system: the Desktop shell's own platform, else the browser's (from
 * `navigator.userAgentData`, then `navigator.platform`); `null` when it cannot be told.
 */
export function userPlatform(): DesktopPlatform | null {
  if (isElectronDesktop()) return electronPlatform();
  if (typeof navigator === 'undefined') return null;
  const { userAgentData } = navigator as Navigator & { userAgentData?: { platform?: string } };
  const name = userAgentData?.platform || navigator.platform || navigator.userAgent;
  if (/Mac|iPhone|iPad|iPod/i.test(name)) return 'macos';
  if (/Win/i.test(name)) return 'windows';
  if (/Linux|X11|CrOS/i.test(name)) return 'linux';
  return null;
}

/** Whether the user is on macOS, where shortcuts are ⌘ chords rather than Ctrl ones. */
export function isMacPlatform(): boolean {
  return userPlatform() === 'macos';
}

/** A keyboard shortcut as data, formatted for the user's platform at render time. */
export type KeyboardShortcut = {
  key: string;
  /** ⌘ on macOS, Ctrl elsewhere. */
  commandOrControl?: boolean | undefined;
  shift?: boolean | undefined;
  alt?: boolean | undefined;
};

type ModifierEvent = Pick<KeyboardEvent, 'metaKey' | 'ctrlKey'>;
type ShortcutEvent = Pick<KeyboardEvent, 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'key'> &
  Partial<Pick<KeyboardEvent, 'code'>>;

/**
 * Whether the platform's primary modifier is held: ⌘ (without Ctrl) on macOS, where Ctrl chords
 * stay the text fields' own editing keys, and Ctrl elsewhere.
 */
export function hasPrimaryModifier(event: ModifierEvent, mac = isMacPlatform()): boolean {
  return mac ? event.metaKey && !event.ctrlKey : event.ctrlKey;
}

/**
 * Whether a key press is the shortcut. With ⌥ held, the physical key (`code`) also matches, since
 * ⌥ on macOS changes the character a letter types.
 */
export function matchesShortcut(
  event: ShortcutEvent,
  shortcut: KeyboardShortcut,
  mac = isMacPlatform(),
): boolean {
  const key = shortcut.key.toLowerCase();
  const physical = /^[a-z]$/.test(key) ? `Key${key.toUpperCase()}` : `Digit${key}`;
  const sameKey =
    event.key.toLowerCase() === key || (Boolean(shortcut.alt) && event.code === physical);
  return (
    sameKey &&
    Boolean(shortcut.commandOrControl) === hasPrimaryModifier(event, mac) &&
    Boolean(shortcut.shift) === event.shiftKey &&
    Boolean(shortcut.alt) === event.altKey
  );
}

// U+FE0E keeps ↩ a glyph of the text font: alone, macOS draws it as an emoji.
const MAC_KEYS: Readonly<Record<string, string>> = {
  Enter: '↩\uFE0E',
  Backspace: '⌫',
  Delete: '⌦',
  Escape: '⎋',
  Tab: '⇥',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
};

const OTHER_KEYS: Readonly<Record<string, string>> = {
  Escape: 'Esc',
  ArrowUp: 'Up',
  ArrowDown: 'Down',
  ArrowLeft: 'Left',
  ArrowRight: 'Right',
};

/** A shortcut as the platform writes it: `⇧⌘G` on macOS, `Ctrl+Shift+G` on Windows and Linux. */
export function shortcutLabel(shortcut: KeyboardShortcut, mac = isMacPlatform()): string {
  const key = shortcut.key.length === 1 ? shortcut.key.toUpperCase() : shortcut.key;
  if (mac) {
    return `${shortcut.alt ? '⌥' : ''}${shortcut.shift ? '⇧' : ''}${
      shortcut.commandOrControl ? '⌘' : ''
    }${MAC_KEYS[key] ?? key}`;
  }
  return [
    shortcut.commandOrControl && 'Ctrl',
    shortcut.alt && 'Alt',
    shortcut.shift && 'Shift',
    OTHER_KEYS[key] ?? key,
  ]
    .filter(Boolean)
    .join('+');
}
